@file:Suppress("TooGenericExceptionThrown")

package com.klantenstop.web.data.repos

import com.klantenstop.web.data.NetworkingConfig
import com.klantenstop.web.data.deleteRequest
import com.klantenstop.web.data.extensions.buildFormData
import com.klantenstop.web.data.getRequest
import com.klantenstop.web.data.models.NetworkResponse
import com.klantenstop.web.data.models.domain.Category
import com.klantenstop.web.data.models.domain.toCategory
import com.klantenstop.web.data.models.dto.requests.categories.InsertCategory
import com.klantenstop.web.data.models.dto.requests.categories.UpdateCategory
import com.klantenstop.web.data.models.dto.responses.CategoryDto
import com.klantenstop.web.data.models.dto.responses.ErrorResponse
import com.klantenstop.web.data.postRequest
import com.klantenstop.web.data.putRequest
import org.w3c.files.File

object CategoryRepo {
    private val categoryUrl = "${NetworkingConfig.baseUrl}/api/v1/categories"

    suspend fun getCategories(): List<Category> {
        val response: NetworkResponse<List<CategoryDto>, ErrorResponse> = getRequest(categoryUrl)

        return when (response) {
            is NetworkResponse.Success -> response.body.map { it.toCategory() }
            is NetworkResponse.ServerError -> {
                val errorMessage = response.body?.errorDescription ?: response.body?.error ?: "Server fout bij het ophalen van categorieën"
                throw Exception(errorMessage)
            }

            is NetworkResponse.NetworkError -> throw Exception("Netwerkfout: controleer je internetverbinding")
            is NetworkResponse.UnknownError -> throw Exception("Onbekende fout bij het ophalen van categorieën")
        }
    }

    suspend fun getCategoryById(id: String): Category {
        val response: NetworkResponse<CategoryDto, ErrorResponse> = getRequest("$categoryUrl/$id")

        return when (response) {
            is NetworkResponse.Success -> response.body.toCategory()
            is NetworkResponse.ServerError -> {
                val errorMessage = response.body?.errorDescription ?: response.body?.error ?: "Server fout bij het ophalen van categorie"
                throw Exception(errorMessage)
            }

            is NetworkResponse.NetworkError -> throw Exception("Netwerkfout: controleer je internetverbinding")
            is NetworkResponse.UnknownError -> throw Exception("Onbekende fout bij het ophalen van categorie")
        }
    }

    suspend fun insertCategory(category: InsertCategory, logo: File?): Category {
        val formData = buildFormData(
            data = category,
            extraFields = mapOf("image" to logo).filterValues { it != null }
        )

        val response: NetworkResponse<CategoryDto, ErrorResponse> = postRequest(resource = categoryUrl, body = formData)

        return when (response) {
            is NetworkResponse.Success -> response.body.toCategory()
            is NetworkResponse.ServerError -> {
                val errorMessage = response.body?.errorDescription ?: response.body?.error ?: "Server fout bij het aanmaken van categorie"
                throw Exception(errorMessage)
            }

            is NetworkResponse.NetworkError -> throw Exception("Netwerkfout: controleer je internetverbinding")
            is NetworkResponse.UnknownError -> throw Exception("Onbekende fout bij het aanmaken van categorie")
        }
    }

    suspend fun updateCategory(id: String, update: UpdateCategory): Category {
        val response: NetworkResponse<CategoryDto, ErrorResponse> = putRequest(resource = "$categoryUrl/$id", body = update)

        return when (response) {
            is NetworkResponse.Success -> response.body.toCategory()
            is NetworkResponse.ServerError -> {
                val errorMessage = response.body?.errorDescription ?: response.body?.error ?: "Server fout bij het bijwerken van categorie"
                throw Exception(errorMessage)
            }

            is NetworkResponse.NetworkError -> throw Exception("Netwerkfout: controleer je internetverbinding")
            is NetworkResponse.UnknownError -> throw Exception("Onbekende fout bij het bijwerken van categorie")
        }
    }

    suspend fun deleteCategory(id: String) {
        val response: NetworkResponse<Unit, ErrorResponse> = deleteRequest("$categoryUrl/$id")

        when (response) {
            is NetworkResponse.Success -> Unit
            is NetworkResponse.ServerError -> {
                val errorMessage = response.body?.errorDescription ?: response.body?.error ?: "Server fout bij het verwijderen van categorie"
                throw Exception(errorMessage)
            }

            is NetworkResponse.NetworkError -> throw Exception("Netwerkfout: controleer je internetverbinding")
            is NetworkResponse.UnknownError -> throw Exception("Onbekende fout bij het verwijderen van categorie")
        }
    }

    suspend fun updateCategoryImage(id: String, file: File, altText: String): Category {
        val formData = buildFormData(
            extraFields = mapOf("image" to file),
            data = mapOf("altText" to altText)
        )

        val response: NetworkResponse<CategoryDto, ErrorResponse> = putRequest(resource = "$categoryUrl/$id/image", body = formData)

        return when (response) {
            is NetworkResponse.Success -> response.body.toCategory()
            is NetworkResponse.ServerError -> {
                val errorMessage = response.body?.errorDescription ?: response.body?.error ?: "Server fout bij het uploaden van afbeelding"
                throw Exception(errorMessage)
            }

            is NetworkResponse.NetworkError -> throw Exception("Netwerkfout: controleer je internetverbinding")
            is NetworkResponse.UnknownError -> throw Exception("Onbekende fout bij het uploaden van afbeelding")
        }
    }

    suspend fun deleteCategoryImage(id: String): Category {
        val response: NetworkResponse<CategoryDto, ErrorResponse> = deleteRequest(resource = "$categoryUrl/$id/image")

        return when (response) {
            is NetworkResponse.Success -> response.body.toCategory()
            is NetworkResponse.ServerError -> {
                val errorMessage = response.body?.errorDescription ?: response.body?.error ?: "Server fout bij het verwijderen van afbeelding"
                throw Exception(errorMessage)
            }

            is NetworkResponse.NetworkError -> throw Exception("Netwerkfout: controleer je internetverbinding")
            is NetworkResponse.UnknownError -> throw Exception("Onbekende fout bij het verwijderen van afbeelding")
        }
    }
}
